import { getProvider } from "fileParsing/definitions";
import { Category } from "fileParsing/types";
import { useTheme } from "@mui/material";
import type { ApplicationState } from "@store/index";
import { OptionKey } from "@store/options/types";
import { FileCategory } from "constants/filenames";
import Tooltip, { type TooltipTexts } from "containers/Components/Tooltip";
import type { HierarchyRectangularNode } from "d3";
import { useMemo } from "react";
import { useAppSelector } from "../../../index";
import { type FilePiece, getFileSize } from ".";

type ToolTipSettings = {
  tooltipWidth: number;
  margin: number;
  showImages: boolean;
};

type Props = {
  visible: boolean;
  settings: ToolTipSettings;
  position: { x: number; y: number };
  files?: HierarchyRectangularNode<FilePiece>;
  isFixed?: boolean;
};

function TooltipWrapper({ visible, settings, position, files, isFixed }: Props) {
  const theme = useTheme();

  const isPrivate = useAppSelector(
    (state: ApplicationState) => state.options.options[OptionKey.HideSensitiveData].value,
  );

  const getPath = (f: HierarchyRectangularNode<FilePiece>) => {
    const id = f.data?.path ?? "/";
    return id.replace(new RegExp(`treemap(\\/${getProvider(f.data.providerId).name}(\\/[^\\/]*))*\\/*`), "");
  };

  const category = files?.data.dataCategory || Category.Other;

  const texts = useMemo<TooltipTexts>(() => {
    try {
      if (files) {
        const provider = getProvider(files.data.providerId);

        if (files.data.files.length > 1) {
          // Group of files
          return {
            title: `${files.data.files.length} Files`,
            subtitle: provider.name,
            body: <>Various files in folder: {files.data.groupBy === "folder" ? files.data.folder : "/"}</>,
            body2: `File Path: /${getPath(files)}\nFile Size: ${getFileSize(files.value ?? 0)}`,
          };
        } else if (files.data.datalength > 1) {
          // File with data elements
          const subc =
            files.data.datacategories[0][0] +
            (files.data.datacategories.length > 1 ? ` and ${files.data.datacategories.length - 1} others.` : "");
          const subcats = subc.startsWith(files.data.providerId) ? subc.substring(files.data.providerId.length) : subc;

          return {
            title: files.data.fileName,
            subtitle: provider.name,
            body: (
              <>
                Contains {files.data.datalength}{" "}
                <span style={{ color: theme.palette.categories[category].main }}>{files.data.dataCategory}</span>{" "}
                elements of type {subcats}
              </>
            ),
            body2: `File Path: /${getPath(files)}\nFile Size: ${getFileSize(files.data.uncompressedSize)}`,
          };
        } else if (files.data.fileType === FileCategory.Picture) {
          // Picture
          return {
            title: files.data.fileName,
            subtitle: provider.name,
            body: (
              <>
                {files.data.fileType} from {provider.name}
                {!settings.showImages && files.data.binaryData ? (
                  <span
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <a href={files.data.binaryData} target="_blank" rel="noopener noreferrer">
                      <img
                        src={files.data.binaryData}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "20vh",
                          filter: isPrivate ? "blur(20px)" : "inherit",
                        }}
                      />
                    </a>
                  </span>
                ) : null}
              </>
            ),
            body2: `File Path: /${getPath(files)}\nFile Size: ${getFileSize(files.value ?? 0)}`,
          };
        } else {
          // File without data elements
          return {
            title: files.data.fileName,
            subtitle: provider.name,
            body: (
              <>
                {files.data.fileType} from {provider.name}
              </>
            ),
            body2: `File Path: /${getPath(files)}\nFile Size: ${getFileSize(files.value ?? 0)}`,
          };
        }
      }
    } catch (e) {
      console.warn("Something went wrong while creating the tooltip:", String(e));
    }

    return {
      title: "",
      subtitle: "",
      body: "",
    };
  }, [files, category, isPrivate, settings.showImages, theme.palette.categories]);

  return (
    <Tooltip
      visible={visible}
      position={position}
      settings={settings}
      texts={texts}
      category={category}
      iconType={"file"}
      isFixed={isFixed}
    />
  );
}

export default TooltipWrapper;
